/* eslint-disable react-hooks/rules-of-hooks */
import useSuaraPushStaf from '../../hooks/useSuaraPushStaf';
import NotifikasiPush from './NotifikasiPush';
import CampaignOutlined from '@mui/icons-material/CampaignOutlined';
import EventSeatOutlined from '@mui/icons-material/EventSeatOutlined';
import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Avatar,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  List,
  BottomNavigation,
  BottomNavigationAction,
  useTheme,
  useMediaQuery,
  IconButton,
  Badge,
  Tooltip,
  Typography,
  AppBar,
  Toolbar,
  Paper,
  Divider,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Bluetooth as BluetoothIcon,
  HomeOutlined,
  ReceiptOutlined,
  PersonOutlined,
  LogoutOutlined,
  DescriptionOutlined,
  TableRestaurantOutlined
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useBranding, useDynamicFavicon } from '../../hooks/useBranding';
import ScreenShareOutlined from '@mui/icons-material/ScreenShareOutlined';
import { bukaLayarPelanggan } from '../../utils/layarServer';
import { useKeluarCekShift } from '../shift/ShiftKasirWeb';

const LayoutKasir = ({ 
  children, 
  onViewChange, 
  onNavigate,
  onLogout,
  showPrinterSettings,
  onShowPrinterSettings,
  printerConnected = false,
  printerInstance,
  onSetPrinterInstance,
  hideLayoutMeja = false
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector((state) => state.auth.user);
  const { logoUrl } = useBranding();
  useDynamicFavicon();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  useSuaraPushStaf(); // suara/ucapan saat Web Push pesanan baru masuk ke tab yang terbuka
  
  // ✅ Helper function untuk derive currentView dari path
  const getViewFromPath = (path) => {
    if (path === '/produkpercabang') return 'home';
    if (path === '/transaksikasir') return 'history';
    if (path === '/notification' || path === '/notification-kasir') return 'notification';
    if (path === '/detail' || path === '/detail-kasir') return 'detail';
    if (path === '/antrian-kasir') return 'antrian';
    if (path === '/piutang-kasir') return 'piutang';
    if (path === '/reservasi-kasir') return 'reservasi';
    if (path === '/layoutmeja') return 'layoutMeja';
    if (path === '/profilekasir') return 'profile';
    return 'notification'; // default
  };
  
  const [currentView, setCurrentView] = useState(() => getViewFromPath(location.pathname));
  const branchNameDisplay = user?.cabang?.namacabang || user?.cabang?.namaCabang || 'Nama Toko';
  
  const handleNavClick = (path, view) => {
    onNavigate?.(path);
    onViewChange?.(view);
  };
  
  // Kasir yang keluar saat shift masih terbuka ditawari menutup shift dulu (shift lupa ditutup membuat kas bercampur).
  const { mintaKeluar, dialog: dialogShiftKeluar } = useKeluarCekShift(user?.role === 'kasir', () => onLogout?.(), { namaKasir: user?.nama || user?.username });
  const handleLogoutClick = () => {
    mintaKeluar();
  };

  useEffect(() => {
    // ✅ Update currentView saat location berubah
    setCurrentView(getViewFromPath(location.pathname));
  }, [location.pathname]);

  const handleNavigate = (path) => {
    navigate(path);
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', flexDirection: 'column' }}>
      {dialogShiftKeluar}
      {/* Top AppBar */}
      <AppBar position="fixed" sx={{ backgroundColor: '#337ab7', zIndex: 1300 }}>
        <Toolbar>
          {logoUrl ? (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
              <Box
                component="img"
                src={logoUrl}
                alt="Logo"
                sx={{ height: 36, maxWidth: 160, objectFit: 'contain' }}
              />
            </Box>
          ) : (
            <Typography variant="h6" sx={{ flex: 1, fontWeight: 'bold' }}>
              KASIRKU
            </Typography>
          )}
          <Tooltip title="Notifikasi">
            <IconButton color="inherit" onClick={() => handleNavClick('/notification-kasir', 'notification')}>
              <Badge badgeContent={0} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      <Box sx={{ display: 'flex', flex: 1, mt: '64px' }}>
        {/* Sidebar Desktop */}
        {!isMobile && (
          <Box
            sx={{
              position: 'fixed',
              left: 0,
              top: 64,
              bottom: 0,
              width: 280,
              backgroundColor: 'white',
              borderRight: '1px solid #e0e0e0',
              display: 'flex',
              flexDirection: 'column',
              zIndex: 1200,
              overflowY: 'auto'
            }}
          >
            {/* Sidebar Header */}
            <Box sx={{ p: 3, borderBottom: '1px solid #f0f0f0', backgroundColor: '#fafafa' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ width: 48, height: 48, bgcolor: '#337ab7', fontSize: '16px', fontWeight: 'bold' }}>
                  {user?.nama?.charAt(0) || user?.username?.charAt(0) || 'K'}
                </Avatar>
                <Box>
                  <Typography variant="subtitle1" fontWeight={600} color="#1a1a1a">
                    {user?.nama || user?.username || 'Kasir'}
                  </Typography>
                  <Typography variant="caption" color="#666">
                    {user?.role || 'Staff Kasir'}
                  </Typography>
                  <Typography variant="subtitle2" color="#666">
                    Cabang: {branchNameDisplay}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Printer Status */}
            <Box sx={{ px: 2, py: 2 }}>
              <Chip
                icon={<BluetoothIcon />}
                label={printerConnected ? "Printer Terhubung" : "Printer Offline"}
                color={printerConnected ? "success" : "default"}
                variant="outlined"
                size="small"
                sx={{ width: '100%', justifyContent: 'flex-start', borderRadius: '8px' }}
              />
              <Chip
                icon={<ScreenShareOutlined />}
                label="Layar Pelanggan"
                variant="outlined"
                size="small"
                onClick={bukaLayarPelanggan}
                sx={{ width: '100%', justifyContent: 'flex-start', borderRadius: '8px', mt: 1 }}
              />
            </Box>

            <Divider sx={{ my: 1 }} />

            {/* Navigation Menu */}
            <List sx={{ pt: 1, px: 1, flex: 1 }}>
              <ListItemButton
                selected={currentView === 'home'}
                onClick={() => handleNavClick('/produkpercabang', 'home')}
                sx={{
                  mx: 1, borderRadius: '12px', mb: 1, height: 52,
                  '&.Mui-selected': { backgroundColor: '#337ab7', color: 'white',
                    '&:hover': { backgroundColor: '#286090' },
                    '& .MuiListItemIcon-root': { color: 'white' }
                  },
                  '&:hover': { backgroundColor: '#e8f4fd' }
                }}
              >
                <ListItemIcon sx={{ minWidth: 45 }}><HomeOutlined /></ListItemIcon>
                <ListItemText primary="Home" primaryTypographyProps={{ fontWeight: currentView === 'home' ? 600 : 500, fontSize: '15px' }} />
              </ListItemButton>

              <ListItemButton
                selected={currentView === 'history'}
                onClick={() => handleNavClick('/transaksikasir', 'history')}
                sx={{
                  mx: 1, borderRadius: '12px', mb: 1, height: 52,
                  '&.Mui-selected': { backgroundColor: '#337ab7', color: 'white',
                    '&:hover': { backgroundColor: '#286090' },
                    '& .MuiListItemIcon-root': { color: 'white' }
                  },
                  '&:hover': { backgroundColor: '#e8f4fd' }
                }}
              >
                <ListItemIcon sx={{ minWidth: 45 }}><ReceiptOutlined /></ListItemIcon>
                <ListItemText primary="Transaksi" primaryTypographyProps={{ fontWeight: currentView === 'history' ? 600 : 500, fontSize: '15px' }} />
              </ListItemButton>

              <ListItemButton
                selected={currentView === 'notification'}
                onClick={() => handleNavClick('/notification-kasir', 'notification')}
                sx={{
                  mx: 1, borderRadius: '12px', mb: 1, height: 52,
                  '&.Mui-selected': { backgroundColor: '#337ab7', color: 'white',
                    '&:hover': { backgroundColor: '#286090' },
                    '& .MuiListItemIcon-root': { color: 'white' }
                  },
                  '&:hover': { backgroundColor: '#e8f4fd' }
                }}
              >
                <ListItemIcon sx={{ minWidth: 45 }}><NotificationsIcon /></ListItemIcon>
                <ListItemText primary="Notifikasi" primaryTypographyProps={{ fontWeight: currentView === 'notification' ? 600 : 500, fontSize: '15px' }} />
              </ListItemButton>

              <ListItemButton
                selected={currentView === 'antrian'}
                onClick={() => handleNavClick('/antrian-kasir', 'antrian')}
                sx={{
                  mx: 1, borderRadius: '12px', mb: 1, height: 52,
                  '&.Mui-selected': { backgroundColor: '#337ab7', color: 'white',
                    '&:hover': { backgroundColor: '#286090' },
                    '& .MuiListItemIcon-root': { color: 'white' }
                  },
                  '&:hover': { backgroundColor: '#e8f4fd' }
                }}
              >
                <ListItemIcon sx={{ minWidth: 45 }}><CampaignOutlined /></ListItemIcon>
                <ListItemText primary="Antrian" primaryTypographyProps={{ fontWeight: currentView === 'antrian' ? 600 : 500, fontSize: '15px' }} />
              </ListItemButton>

              <ListItemButton
                selected={currentView === 'detail'}
                onClick={() => handleNavClick('/detail-kasir', 'detail')}
                sx={{
                  mx: 1, borderRadius: '12px', mb: 1, height: 52,
                  '&.Mui-selected': { backgroundColor: '#337ab7', color: 'white',
                    '&:hover': { backgroundColor: '#286090' },
                    '& .MuiListItemIcon-root': { color: 'white' }
                  },
                  '&:hover': { backgroundColor: '#e8f4fd' }
                }}
              >
                <ListItemIcon sx={{ minWidth: 45 }}><DescriptionOutlined /></ListItemIcon>
                <ListItemText primary="Order Detail" primaryTypographyProps={{ fontWeight: currentView === 'detail' ? 600 : 500, fontSize: '15px' }} />
              </ListItemButton>

              <ListItemButton
                selected={currentView === 'piutang'}
                onClick={() => handleNavClick('/piutang-kasir', 'piutang')}
                sx={{
                  mx: 1, borderRadius: '12px', mb: 1, height: 52,
                  '&.Mui-selected': { backgroundColor: '#337ab7', color: 'white',
                    '&:hover': { backgroundColor: '#286090' },
                    '& .MuiListItemIcon-root': { color: 'white' }
                  },
                  '&:hover': { backgroundColor: '#e8f4fd' }
                }}
              >
                <ListItemIcon sx={{ minWidth: 45 }}><ReceiptOutlined /></ListItemIcon>
                <ListItemText primary="Piutang" primaryTypographyProps={{ fontWeight: currentView === 'piutang' ? 600 : 500, fontSize: '15px' }} />
              </ListItemButton>

              {/* Reservasi: hanya cabang cafe yang paketnya memiliki fitur Reservasi */}
              {user?.cabang?.tipe_cabang === 'cafe' && (!user?.langganan || user.langganan.fitur.includes('reservasi')) && (
                <ListItemButton
                  selected={currentView === 'reservasi'}
                  onClick={() => handleNavClick('/reservasi-kasir', 'reservasi')}
                  sx={{
                    mx: 1, borderRadius: '12px', mb: 1, height: 52,
                    '&.Mui-selected': { backgroundColor: '#337ab7', color: 'white',
                      '&:hover': { backgroundColor: '#286090' },
                      '& .MuiListItemIcon-root': { color: 'white' }
                    },
                    '&:hover': { backgroundColor: '#e8f4fd' }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 45 }}><EventSeatOutlined /></ListItemIcon>
                  <ListItemText primary="Reservasi" primaryTypographyProps={{ fontWeight: currentView === 'reservasi' ? 600 : 500, fontSize: '15px' }} />
                </ListItemButton>
              )}

              {/* ✅ Hide Layout Meja jika hideLayoutMeja={true} */}
              {!hideLayoutMeja && (
                <ListItemButton
                  selected={currentView === 'layoutMeja'}
                  onClick={() => handleNavClick('/layoutmeja', 'layoutMeja')}
                  sx={{
                    mx: 1, borderRadius: '12px', mb: 1, height: 52,
                    '&.Mui-selected': { backgroundColor: '#337ab7', color: 'white',
                      '&:hover': { backgroundColor: '#286090' },
                      '& .MuiListItemIcon-root': { color: 'white' }
                    },
                    '&:hover': { backgroundColor: '#e8f4fd' }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 45 }}><TableRestaurantOutlined /></ListItemIcon>
                  <ListItemText primary="Layout Meja" primaryTypographyProps={{ fontWeight: currentView === 'layoutMeja' ? 600 : 500, fontSize: '15px' }} />
                </ListItemButton>
              )}

              <ListItemButton
                selected={currentView === 'profile'}
                onClick={() => handleNavClick('/profilekasir', 'profile')}
                sx={{
                  mx: 1, borderRadius: '12px', mb: 1, height: 52,
                  '&.Mui-selected': { backgroundColor: '#337ab7', color: 'white',
                    '&:hover': { backgroundColor: '#286090' },
                    '& .MuiListItemIcon-root': { color: 'white' }
                  },
                  '&:hover': { backgroundColor: '#e8f4fd' }
                }}
              >
                <ListItemIcon sx={{ minWidth: 45 }}><PersonOutlined /></ListItemIcon>
                <ListItemText primary="Profile" primaryTypographyProps={{ fontWeight: currentView === 'profile' ? 600 : 500, fontSize: '15px' }} />
              </ListItemButton>

              <Divider sx={{ my: 2 }} />

              <ListItemButton
                onClick={handleLogoutClick}
                sx={{
                  mx: 1, borderRadius: '12px', height: 52, color: '#ff4444',
                  '&:hover': { backgroundColor: '#ffebee' }
                }}
              >
                <ListItemIcon sx={{ minWidth: 45, color: '#ff4444' }}><LogoutOutlined /></ListItemIcon>
                <ListItemText primary="Logout" primaryTypographyProps={{ fontWeight: 500, fontSize: '15px' }} />
              </ListItemButton>
            </List>

            {/* Footer */}
            <Box sx={{ p: 2, borderTop: '1px solid #f0f0f0', backgroundColor: '#fafafa' }}>
              <NotifikasiPush sx={{ mb: 1 }} />
              <Typography variant="caption" color="#666" align="center" display="block">
                KASIRKU
              </Typography>
              <Button
                variant="outlined"
                startIcon={<BluetoothIcon />}
                onClick={() => onShowPrinterSettings?.(true)}
                sx={{ mt: 2, borderRadius: '12px', width: '100%' }}
                color={printerConnected ? 'success' : 'primary'}
              >
                {printerConnected ? 'Printer Terhubung' : 'Hubungkan Printer'}
              </Button>
            </Box>
          </Box>
        )}

        {/* Main Content Area - PERBAIKAN DI SINI */}
        <Box sx={{ 
          flex: 1, 
          ml: isMobile ? 0 : '280px', 
          width: '100%',
          pb: isMobile ? '56px' : 0 // Tambah padding bottom untuk mobile agar tidak tertutup navbar
        }}>
          {/* Render children di sini */}
          {children}
        </Box>
      </Box>

      {/* Bottom Navigation untuk Mobile - DIPINDAH KE LUAR CHILDREN */}
      {isMobile && (
        <Paper sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1300 }} elevation={3}>
          <BottomNavigation
            value={currentView}
            onChange={(event, newValue) => {
              if (newValue === 'home') {
                handleNavigate('/produkpercabang');
                setCurrentView('home');
              } else if (newValue === 'history') {
                handleNavigate('/transaksikasir');
                setCurrentView('history');
              } else if (newValue === 'notification') {
                handleNavigate('/notification-kasir');
                setCurrentView('notification');
              } else if (newValue === 'profile') {
                handleNavigate('/profilekasir');
                setCurrentView('profile');
              }
            }}
            showLabels
          >
            <BottomNavigationAction
              label="Home"
              value="home"
              icon={<HomeOutlined />}
            />
            <BottomNavigationAction
              label="Transaksi"
              value="history"
              icon={<ReceiptOutlined />}
            />
            <BottomNavigationAction
              label="Notifikasi"
              value="notification"
              icon={<NotificationsIcon />}
            />
            <BottomNavigationAction
              label="Profile"
              value="profile"
              icon={<PersonOutlined />}
            />
          </BottomNavigation>
        </Paper>
      )}
    </Box>
  )
}

export default LayoutKasir